import { useMemo } from 'react'

import { Button } from '@circlefin/components'
import useTranslation from 'next-translate/useTranslation'

import { useSettlementCreditLine } from '../../hooks/settlement-credit-line'
import { CopyAllButton } from '../CopyAllButton/CopyAllButton'
import { CopyGrid } from '../CopyGrid/CopyGrid'
import { ReferenceIdBanner } from '../ReferenceIdBanner/ReferenceIdBanner'

import type { SettlementCreditWireInstruction } from '@shared/graphql'

export interface DepositInstructionsContentProps {
  /**
   * Settlement Credit Wire Instructions.
   */
  wireInstruction?: SettlementCreditWireInstruction
  /**
   * Flag to check if the query is still loading.
   */
  loading: boolean
  /**
   * CTA Button Text.
   */
  ctaButtonText: string
  /**
   * Callback to handle the continue button click.
   */
  onContinue?: React.MouseEventHandler<HTMLButtonElement>
}

export const DepositInstructionsContent: React.FC<
  DepositInstructionsContentProps
> = ({ wireInstruction, loading, ctaButtonText, onContinue }) => {
  const { translationKey } = useSettlementCreditLine()
  const { t } = useTranslation(translationKey)

  const gridItems = useMemo(() => {
    return [
      {
        label: t('request.transferDetails.labels.currency'),
        id: 'currency',
        value: wireInstruction?.beneficiaryBank.currency,
      },
      {
        label: t('request.transferDetails.labels.bankAccountNumber'),
        id: 'bankAccountNumber',
        value: wireInstruction?.beneficiaryBank.accountNumber,
      },
      {
        label: t('request.transferDetails.labels.routingNumber'),
        id: 'routingNumber',
        value: wireInstruction?.beneficiaryBank.routingNumber,
      },
      {
        label: t('request.transferDetails.labels.swiftCode'),
        id: 'swiftCode',
        value: wireInstruction?.beneficiaryBank.swiftCode,
      },
      {
        label: t('request.transferDetails.labels.beneficiaryName'),
        id: 'beneficiaryName',
        value: wireInstruction?.beneficiary.name,
      },
      {
        label: t('request.transferDetails.labels.beneficiaryAddress'),
        id: 'beneficiaryAddress',
        value: `${wireInstruction?.beneficiary.address1}, ${wireInstruction?.beneficiary.address2}`,
      },
      {
        label: t('request.transferDetails.labels.bankName'),
        id: 'bankName',
        value: wireInstruction?.beneficiaryBank.name,
      },
    ]
  }, [t, wireInstruction])

  return (
    <div className="flex w-112 flex-col">
      <div className="mb-4 flex flex-col rounded-sm border border-neutral-subtle p-6 pr-4">
        <div className="mb-10 flex justify-between pr-2 type-body-base-bold">
          <h4 className="text-neutral-strong">
            {t('request.transferDetails.title')}
          </h4>
          <CopyAllButton items={gridItems} />
        </div>
        <div className="flex flex-col gap-y-4 font-circular">
          <CopyGrid gridItems={gridItems} loading={loading} />
        </div>
      </div>
      <div className="flex flex-col gap-y-4 font-circular">
        <ReferenceIdBanner
          referenceId={wireInstruction?.trackingRef}
          virtualAccountEnabled={wireInstruction?.virtualAccountEnabled}
        />
        <Button
          className="w-full"
          data-testid="continue-button"
          disabled={loading}
          onClick={onContinue}
          variant="primary"
        >
          {ctaButtonText}
        </Button>
      </div>
    </div>
  )
}
